import { useGT } from 'gt-react';
import { CheckIcon, CopyIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { downloadCSV } from '@/utils/csvDownload';
import { toClipboardText, toCsvRecords } from './tableToolResultData';
import { useChatCopy } from './useChatCopy';

export function TableExportActions({
  columns,
  rows,
  title,
}: {
  columns: string[];
  rows: string[][];
  title?: string;
}) {
  const gt = useGT();
  const { copied, copy } = useChatCopy('table', 1500);

  function handleDownload() {
    const stem = (title ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    downloadCSV({
      data: toCsvRecords(columns, rows),
      filename: stem || 'table',
      includeTotals: false,
    });
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void copy(toClipboardText(columns, rows))}
        aria-label={
          copied
            ? gt('Table copied to clipboard', {
                context:
                  'aria-label on the copy button of an AI-rendered table once the copy succeeded',
              })
            : gt('Copy table', {
                context:
                  'aria-label on the copy button of an AI-rendered table',
              })
        }
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5" />
        ) : (
          <CopyIcon className="h-3.5 w-3.5" />
        )}
        {copied
          ? gt('Copied', {
              context: 'confirmation after copying an AI-rendered table',
            })
          : gt('Copy', {
              context: 'button copying an AI-rendered table to the clipboard',
            })}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        aria-label={gt('Save table as CSV', {
          context: 'aria-label on the CSV download button of an AI table',
        })}
      >
        <DownloadSimpleIcon className="h-3.5 w-3.5" />
        {gt('CSV', {
          context: 'button downloading an AI-rendered table as a CSV file',
        })}
      </Button>
    </div>
  );
}
